import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  Header,
  TypeOfTableData
} from '../../pricing-simulator/types/generic-table.model';
import {
  fetchRenovationTableData,
  fetchSimulatedRenovationTableData,
  resetSimulationData,
  saveRenovationSimulation
} from '../api/renovation-api';
import { filterColumnsForToggleView } from '../../pricing-simulator/utils/column-utils';
import { SummaryStripModuleObject } from '../../constants/constant';
import { updateDropdownSelection } from '../../common/features/table/store/table-slice';
import { calculateGlobalValues } from '../../pricing-simulator/utils/price-action-utils';

const getRenovationTableData = createAsyncThunk(
  'getRenovationTableData',
  async ({
    dataInput,
    sourceId,
    moduleId,
    scenarioId
  }: {
    dataInput: any;
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  }, thunkAPI) => {
    const isSimulation =
      (dataInput.row && dataInput.row.data && dataInput.row.data.length) ||
      (dataInput.row && dataInput.row.inputs && dataInput.row.inputs.length);
    // TODO: Check Two functions are called here based on the condition. Use only one function.
    const response = isSimulation
      ? await fetchSimulatedRenovationTableData(
        dataInput,
        sourceId,
        moduleId,
        scenarioId
      )
      : await fetchRenovationTableData(
        dataInput,
        sourceId,
        moduleId,
        scenarioId
      );
    const { globalPA1Value } = calculateGlobalValues(
      response.row,
      'renovation_implement_after_cal'
    );

    // Dispatch actions to update Redux state
    thunkAPI.dispatch(
      updateDropdownSelection({
        columnKey: 'pricing_implement_after_cal_1',
        value: globalPA1Value,
      })
    );
    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      ''
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );
    return {
      response,
      resultCols,
      inputCols,
      isSimulation
    };
  }
);

const resetRenovationSimulationData = createAsyncThunk(
  'getRenovationTableData',
  async ({
    sourceId,
    moduleId,
    scenarioId
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  },
    thunkAPI
  ) => {
    const response = await resetSimulationData(sourceId, moduleId, scenarioId);
    const { globalPA1Value } = calculateGlobalValues(
      response.row,
      'renovation_implement_after_cal'
    );

    // Dispatch actions to update Redux state
    thunkAPI.dispatch(
      updateDropdownSelection({
        columnKey: 'pricing_implement_after_cal_1',
        value: globalPA1Value,
      })
    );
    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],

      response.header,

      ''
    );

    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );

    return {
      resultCols,
      inputCols,
      response
    };
  }
);

const saveRenovationSimulationData = createAsyncThunk(
  'saveRenovationSimulationData',
  async ({
    sourceId,
    moduleId,
    scenarioId
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  }) => {
    const response = await saveRenovationSimulation(
      sourceId,
      moduleId,
      scenarioId
    );
    return response;
  }
);

interface IRenovationState {
  fetchingTableData: boolean;
  renovationTableData?: any;
  inputCols: Header[];
  isSimulationSaved: boolean;
  simulatedRow: any;
  resultCols: Header[];
  isSimulating: boolean;
  isSimulationFailed: boolean;
  toggleData: { [key: string]: string }[];
  isScenarioSavedSuccessfully: boolean;
  isScenarioSaving: boolean;
  enableSimulation: boolean;
  isSKUEditedClick: boolean;
  getToggleLabelName: string;
  lastSimulatedSnapshot: any | null;
}

const initialState: IRenovationState = {
  fetchingTableData: false,
  isScenarioSaving: false,
  isSimulationFailed: false,
  isSKUEditedClick: false,
  enableSimulation: false,
  isScenarioSavedSuccessfully: false,
  simulatedRow: [],
  toggleData: [],
  isSimulationSaved: false,
  isSimulating: false,
  inputCols: [],
  resultCols: [],
  renovationTableData: {
    legend: { metadata: [] },
    ownKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    retailerKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    metadata: [],
    show_edited: { enabled: false, label: '' },
    row: [],
    toggle: {
      metadata: [],
      data: {}
    },
    output_toggle: {
      metadata: [],
      data: {}
    },
    summary_strip: {
      metadata: [],
      data: {
        internal_dt: 0,
        external_dt: 0,
        renovation: SummaryStripModuleObject,
        pricing: SummaryStripModuleObject,
        delist: SummaryStripModuleObject,
        distribution: SummaryStripModuleObject,
        innovation_table: SummaryStripModuleObject
      }
    },
    footer: {
      metadata: {
        enabled: false,
        label: ''
      },
      row: []
    },
    header: []
  },
  getToggleLabelName: '',
  lastSimulatedSnapshot: null
};

const renovationSlice = createSlice({
  name: 'renovation',
  initialState,
  reducers: {
    resetRenovationSimulation(state) {
      state.enableSimulation = false;
    },
    handleRenovationSimulationFlagValue(state) {
      state.isSimulating = true;
    },
    handleSimulationFailedFlagValue(state) {
      state.isSimulationFailed = false;
    },
    handleDialogCloseEvent(state) {
      state.isScenarioSavedSuccessfully = false;
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getRenovationTableData.pending, (state) => {
        state.fetchingTableData = true;
        state.isSimulationFailed = false;
      })
      .addCase(getRenovationTableData.fulfilled, (state, action) => {
        state.fetchingTableData = false;
        state.isSimulationFailed = false;
        state.renovationTableData = {
          ...action.payload.response
        };
        state.isSKUEditedClick = false;
        if (action.payload.isSimulation) {
          state.enableSimulation = true;
          state.isSimulating = false;
        }
        state.lastSimulatedSnapshot = JSON.parse(
          JSON.stringify(action.payload.response)
        );
      })
      .addCase(getRenovationTableData.rejected, (state) => {
        state.fetchingTableData = false;
        state.isSimulating = false;
        state.isSimulationFailed = true;
      })
      .addCase(saveRenovationSimulationData.pending, (state) => {
        state.isSimulationSaved = true;
        state.isScenarioSavedSuccessfully = false;
        state.isScenarioSaving = true;
      })
      .addCase(saveRenovationSimulationData.fulfilled, (state, action) => {
        state.isSimulationSaved = false;
        state.isScenarioSavedSuccessfully = true;
        state.isScenarioSaving = false;
      })
      .addCase(saveRenovationSimulationData.rejected, (state) => {
        state.isSimulationSaved = false;
        state.isScenarioSaving = false;
      })
});

export const {
  handleRenovationSimulationFlagValue,
  resetRenovationSimulation,
  handleSimulationFailedFlagValue
} = renovationSlice.actions;

export {
  getRenovationTableData,
  resetRenovationSimulationData,
  saveRenovationSimulationData
};

export default renovationSlice;
